import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { reached, removed } from '~/components/use-work'
import { DiaryDay } from '~/components/diary-day'
import { DiaryHead } from '~/components/diary-head'
import { EdgeSteps } from '~/components/edge-steps'
import { EmptyState } from '~/components/empty-state'
import { EntryDialog } from '~/components/entry-dialog'
import { ExpandingAction } from '~/components/expanding-action'
import { FoodMark } from '~/components/food-mark'
import { FoodSheet } from '~/components/food-sheet'
import {
  BarbellIcon,
  CheckIcon,
  ChevronRightIcon,
  DiaryIcon,
  MealIcon,
  RecipeIcon,
  SendIcon,
} from '~/components/icons'
import { MealPage } from '~/components/meal-page'
import { loggedMoment } from '~/lib/meal-moment'
import { RecipeFormDialog } from '~/components/recipe-form-dialog'
import { SendEntries } from '~/components/send-entries'
import { useSelection } from '~/components/selection-context'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useToast } from '~/components/toast-context'
import { recordDiaryStep } from '~/lib/diary-undo'
import { landingOf, type SendKind, type SendWhere } from '~/lib/entry-landing'
import { todayKey } from '~/data/weighins'
import { useApi, useDataState } from '~/data/data-context'
import {
  amountSaid,
  dayTotal,
  goalOn,
  isOnlyBroughtIn,
  macroLine,
  nowTime,
  recipeNameFrom,
  EMPTY_MEAL_STATE,
  type Amount,
  type Entry,
  type Food,
  type Ingredient,
  type Meal,
  type NutritionData,
} from '~/data/nutrition'
import type { Workout } from '~/data/types'
import {
  dropEntries,
  keepDayIncomplete,
  keepEntry,
  reloadNutrition,
  useNutrition,
  useDayEntries,
} from '~/data/nutrition-store'
import { dayKey, dayStart, formatDayLabel, formatHour } from '~/lib/dates'
import { shifted } from '~/lib/day-step'
import { diaryDayLastOn, keepDiaryDay } from '~/lib/diary-day-memory'
import { dialogDayOf, withoutDialog, type DialogSearch } from '~/lib/dialogs'
import { useAuth } from '~/auth/auth-context'
import { handOver, saidOf, sentFromEntries } from '~/lib/share-food'
import { workoutTileIds } from '~/lib/workout-pictures'

interface DiarySearch extends DialogSearch {
  /** The day the diary is open on. Absent means today, which is never written. */
  readonly day?: string
}

export const Route = createFileRoute('/diary')({
  validateSearch: (search: Record<string, unknown>): DiarySearch => {
    const day = dialogDayOf(search.day)
    return day === null ? {} : { day }
  },
  component: Diary,
})

/**
 * The diary, per `/specs/features/nutrition.spec.md`: a page a day, opening on today, headed
 * by the day's total against the goal and listing its entries in the order they were eaten.
 * Nothing about a day is shown until its entries have arrived.
 */
function Diary() {
  const { day: asked } = Route.useSearch()
  const today = todayKey()
  const day = asked ?? today
  const data = useNutrition()
  const dayEntries = useDayEntries(day)
  /*
   * The day's workouts stand among what was eaten — the nutrition spec, Workouts on the day.
   * Behind a setting, because a food log is a food log for plenty of people.
   */
  const { settings } = useAuth()
  const showsWorkouts = settings.workoutsInDiary
  const state = useDataState()
  const { workouts, timelineSettled } = state

  /*
   * The workouts are held to the same rule the entries are: nothing about the day is shown
   * until everything the day is made of has arrived. A list drawn before the timeline settled
   * would say "nothing was trained" out of a list that has not been read yet.
   */
  /*
   * Signed in, and this browser has never read the account: what it holds is nothing yet, not
   * an empty diary. Neither an empty day nor a skeleton that will never fill is an answer, so
   * it says which it is — the same as the timeline.
   */
  if (state.couldNotBeRead) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center text-ink-muted">
        <p className="text-lg text-ink">Your diary hasn’t arrived yet</p>
        <p className="mt-2 text-sm">
          This browser hasn’t been able to reach your account. It will be here once it can.
        </p>
      </div>
    )
  }

  if (data === null || dayEntries === null || (showsWorkouts && !timelineSettled)) {
    return (
      <SkeletonBlock label="Loading diary" className="mx-auto max-w-3xl space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </SkeletonBlock>
    )
  }

  return (
    <DiaryDayPage
      day={day}
      today={today}
      data={data}
      entries={dayEntries}
      workouts={showsWorkouts ? workouts : []}
    />
  )
}

/** One thing standing on the day: an entry, or a workout. */
interface DayItem {
  /** The time it stands at, in the shape an entry's time is written in. */
  readonly at: string
  readonly entry: Entry | null
  readonly workout: Workout | null
}

/** The day, once everything it is made of has arrived. */
function DiaryDayPage({
  day,
  today,
  data,
  entries,
  workouts,
}: {
  day: string
  today: string
  data: NutritionData
  entries: readonly Entry[]
  /** The workouts the diary keeps, which is none of them while the setting is off. */
  workouts: readonly Workout[]
}) {
  const navigate = useNavigate()
  const api = useApi()
  const toast = useToast()
  const { session } = useAuth()

  const head = useRef<HTMLElement>(null)
  /** The day as it is laid out, whose height the head's column takes — see `DiaryHead`. */
  const laidOut = useRef<HTMLDivElement>(null)
  const [opened, setOpened] = useState<Entry | null>(null)
  /*
   * The day last stood on, for coming back to it from another destination — the nutrition
   * spec, The diary. The last day the page rendered on is the one it was left on.
   */
  useEffect(() => {
    keepDiaryDay(day)
  }, [day])
  /*
   * Which side the day came from, for the slide it arrives on: a later day slides in from
   * the right, an earlier one from the left, and the first day arrives without one. Worked
   * out as the day changes under the page, from the day it last stood on.
   */
  const [slide, setSlide] = useState<{ day: string; from: 'left' | 'right' | null }>(() => {
    // The page is remade as a day's entries arrive, so the day it came from is the one the
    // diary remembers standing on, not one this render has seen.
    const before = diaryDayLastOn()
    return {
      day,
      from: before === null || before === day ? null : before < day ? 'right' : 'left',
    }
  })
  if (slide.day !== day) {
    setSlide({ day, from: slide.day < day ? 'right' : 'left' })
  }
  const cameFrom = slide.day === day ? slide.from : null
  /*
   * The choosing is the app's own selection, holding entries — the app spec, Selecting: it is
   * open while anything is chosen, choosing the first entry opens it and putting down the
   * last closes it, and while it is open the header is its.
   */
  const { selection, toggle: toggleChosen, clear: putDown, setActions } = useSelection()
  const selecting = selection?.kind === 'entry'
  const chosen = new Set(selecting ? selection.ids : [])
  const [sharing, setSharing] = useState(false)
  const [makingRecipe, setMakingRecipe] = useState(false)
  /** The meal just made of the chosen entries, open on its page. */
  const [makingMeal, setMakingMeal] = useState<Meal | null>(null)
  const [marking, setMarking] = useState(false)

  const go = (next: string) => {
    void navigate({
      to: '/diary',
      search: (prev) => ({ ...withoutDialog(prev), ...(next === today ? {} : { day: next }) }),
      replace: true,
      resetScroll: false,
    })
  }

  /** An entry opened again, changed on the food's page: kept here at once, and written. */
  const saveEntry = async (entry: Entry, amount: Amount, onDay: string, time: string) => {
    try {
      const saved = await api.putEntry(entry.id, {
        day: onDay,
        time,
        name: entry.name,
        food: entry.food,
        amount,
      })
      keepEntry(saved)
      recordDiaryStep({
        said: `${entry.name} changed`,
        changes: [{ id: entry.id, before: entry, after: saved }],
      })
      reloadNutrition()
      toast('Entry updated.')
      setOpened(null)
    } catch {
      toast('That did not reach us. Try again in a moment.')
    }
  }

  /** Asked from its confirmation, which stays and says so where the server was not reached. */
  const removeEntry = async (entry: Entry) => {
    await reached(() => removed(() => api.deleteEntry(entry.id)))
    dropEntries([entry.id])
    recordDiaryStep({
      said: `${entry.name} removed`,
      changes: [{ id: entry.id, before: entry, after: null }],
    })
    reloadNutrition()
    toast(`${entry.name} removed.`)
    setOpened(null)
  }

  const dayWorkouts = workouts.filter((workout) => dayKey(workout.startedAt) === day)
  /** Entries and workouts on one list, in the order the day happened. */
  const laid: DayItem[] = [
    ...entries.map((entry) => ({ at: entry.time, entry, workout: null })),
    ...dayWorkouts.map((workout) => ({
      at: timeOf(workout.startedAt),
      entry: null,
      workout,
    })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
  /*
   * The day laid out on its hours — the nutrition spec, The diary: each hour something stands
   * in is a card of its own, in the order of the day. Only the hours holding something: the
   * phone draws every hour of a waking day because entries are dragged onto them there, and
   * here they are not.
   */
  const hours = laid.reduce<{ hour: number; items: DayItem[] }[]>((held, item) => {
    const hour = Number(item.at.slice(0, 2))
    const last = held[held.length - 1]
    if (last?.hour === hour) {
      last.items.push(item)
      return held
    }
    return [...held, { hour, items: [item] }]
  }, [])
  const total = dayTotal(entries)
  const goal = goalOn(data.goals, day)
  const incomplete = data.incompleteDays.includes(day)
  const picked = entries.filter((entry) => chosen.has(entry.id))
  /** The days something was trained on, for the quiet mark under the calendar's numbers. */
  const trainedDays = new Set(workouts.map((workout) => dayKey(workout.startedAt)))
  /** A day either side of this one, as the window's edges offer it. */
  const neighbour = (by: -1 | 1) => {
    const at = shifted(day, by)
    const label = by === -1 ? 'The day before' : 'The day after'
    return {
      label,
      title: formatDayLabel(dayStart(at)),
      detail: label,
      go: () => {
        go(at)
      },
    }
  }

  /*
   * What was chosen, sent to somebody to log — the nutrition spec, Sending from the app.
   * Whatever was chosen and however much of it: one entry is an entry share of one member, and
   * the same food chosen twice is two members in the amounts each was logged in.
   *
   * Entries and not a meal. What was chosen already happened, at a time, on a day, and the
   * thing to do with it on the other side is log it — so it travels as the moment it was,
   * carrying the day it stands on and the time of the earliest of it.
   */
  const sendChosenToSomebody = async () => {
    if (picked.length === 0 || sharing) {
      return
    }
    setSharing(true)
    try {
      const earliest = picked.reduce(
        (soonest, entry) => (entry.time < soonest ? entry.time : soonest),
        picked[0]?.time ?? nowTime(),
      )
      const shared = sentFromEntries(picked, session?.user.name ?? null, {
        day,
        time: earliest,
      })
      const outcome = await handOver(
        shared,
        saidOf(shared, dayTotal(picked), session?.user.name ?? null),
      )
      if (outcome === 'copied') {
        toast('Link copied')
      } else if (outcome === 'too-large') {
        toast('There is too much here to send as a link.')
      } else if (outcome === 'failed') {
        toast('That could not be sent.')
      }
    } finally {
      setSharing(false)
    }
  }

  /*
   * A meal made of the chosen entries exists at once, named after them and holding them as
   * slots in the amounts they were logged; its first log is those entries, at the time of the
   * earliest of them; and the day stays as it was — the nutrition spec, How a meal comes to
   * be. Its page then opens.
   */
  const makeAMeal = async () => {
    if (picked.length === 0) {
      return
    }
    const taken = [...picked].sort((a, b) => a.time.localeCompare(b.time))
    const id = crypto.randomUUID()
    const slots = ingredientsOf(taken).map((member) => ({
      id: crypto.randomUUID(),
      alternatives: [{ id: crypto.randomUUID(), member }],
    }))
    try {
      const saved = await api.putMeal(id, {
        name: recipeNameFrom(taken.map((entry) => entry.name)),
        emoji: null,
        slots,
        state: EMPTY_MEAL_STATE,
      })
      const first = taken[0]
      const meal = await api.logMeal(id, {
        id: crypto.randomUUID(),
        loggedAt: loggedMoment(day, first?.time ?? nowTime()),
        name: saved.name,
        emoji: null,
        slots: saved.slots,
        state: saved.state,
      })
      putDown()
      setMakingMeal(meal)
    } catch {
      toast('That did not reach us. Try again in a moment.')
    }
  }

  /*
   * Copying or moving what is taken up — the nutrition spec, Taking entries up. Moving writes
   * each entry back where it is going, keeping its id, so nothing is left behind and nothing
   * is created; copying writes new ones and leaves the originals where they stand.
   *
   * The day stays where it is either way: what was taken up is put down, the asking ends, and
   * the diary does not follow what was sent — somebody copying a breakfast to tomorrow is
   * looking at today, and being carried off it is the app deciding where they wanted to be.
   */
  const sendChosen = async (kind: SendKind, where: SendWhere) => {
    if (picked.length === 0) {
      return
    }
    const taken = [...picked]
    try {
      const landed = await Promise.all(
        taken.map(async (entry) => {
          const { day: onto, time } = landingOf(where, entry.time)
          return {
            was: entry.id,
            onto,
            saved: await api.putEntry(kind === 'move' ? entry.id : crypto.randomUUID(), {
              day: onto,
              time,
              name: entry.name,
              food: entry.food,
              amount: entry.amount,
              // A quick entry carries its own numbers; a food's are worked out from the amount.
              ...(entry.food === null ? { nutrients: entry.nutrients } : {}),
            }),
          }
        }),
      )
      for (const { saved } of landed) {
        keepEntry(saved)
      }
      /*
       * One press, one step: five entries copied to tomorrow are undone by one undo. A move
       * writes each row where it is going and keeps its id, so undoing one is writing it back
       * where it was; a copy made new rows, so undoing one is taking them away again.
       */
      recordDiaryStep({
        said: kind === 'move' ? 'the move' : 'the copy',
        changes: landed.map(({ saved }, at) => ({
          id: saved.id,
          before: kind === 'move' ? (taken[at] ?? null) : null,
          after: saved,
        })),
      })
      // A move off this day takes the row with it; one that landed here is already kept.
      if (kind === 'move') {
        dropEntries(landed.filter((each) => each.onto !== day).map((each) => each.was))
      }
      reloadNutrition()
      const many = taken.length === 1 ? 'Entry' : `${String(taken.length)} entries`
      toast(kind === 'move' ? `${many} moved.` : `${many} copied.`)
    } catch {
      toast('That did not reach us. Try again in a moment.')
    }
    putDown()
  }

  const markIncomplete = async (next: boolean) => {
    setMarking(true)
    try {
      await api.setDayIncomplete(day, next)
      keepDayIncomplete(day, next)
      reloadNutrition()
    } catch {
      toast('That did not reach us. Try again in a moment.')
    } finally {
      setMarking(false)
    }
  }

  /*
   * The chosen entries become one entry of the recipe just saved, in the amount that adds up
   * to what they were — the whole recipe, which is as many servings as it makes — at the time
   * of the earliest of them. The old entries go in one request once the new one is in.
   */
  const collapseInto = async (recipe: Food) => {
    const earliest = [...picked].sort((a, b) => (a.time < b.time ? -1 : 1))[0]
    if (earliest === undefined || recipe.recipe === null) {
      return
    }
    try {
      const saved = await api.putEntry(crypto.randomUUID(), {
        day,
        time: earliest.time,
        food: recipe,
        amount: { value: recipe.recipe.servings, unit: 'portion', portion: 'serving' },
      })
      await api.deleteEntries(picked.map((entry) => entry.id))
      keepEntry(saved)
      dropEntries(picked.map((entry) => entry.id))
      // The one entry made and the several it stood in for are one press, so one undo.
      recordDiaryStep({
        said: `${recipe.name} standing in`,
        changes: [
          { id: saved.id, before: null, after: saved },
          ...picked.map((entry) => ({ id: entry.id, before: entry, after: null })),
        ],
      })
      reloadNutrition()
      toast(`${recipe.name} stands in for ${String(picked.length)} entries.`)
    } catch {
      toast('That did not reach us. Try again in a moment.')
    }
    putDown()
  }

  /** An entry chosen or put down — the same press wherever it lands. */
  const toggle = (entry: Entry) => {
    toggleChosen('entry', entry.id)
  }

  /*
   * What the header offers while entries are chosen. The presses reach the handlers as they
   * stand on the render they happen in, through a ref kept current after each render, so the
   * controls themselves are only made again when what they say changes — a bar remade on
   * every render of the page would have the page and the header chasing each other.
   */
  const latest = useRef({ makeAMeal, sendChosenToSomebody, sendChosen })
  useEffect(() => {
    latest.current = { makeAMeal, sendChosenToSomebody, sendChosen }
  })
  useEffect(() => {
    if (!selecting) {
      return
    }
    setActions(
      <ChosenActions
        data={data}
        sharing={sharing}
        onSend={(kind, where) => latest.current.sendChosen(kind, where)}
        onRecipe={() => {
          setMakingRecipe(true)
        }}
        onMeal={() => latest.current.makeAMeal()}
        onSendToSomebody={() => {
          void latest.current.sendChosenToSomebody()
        }}
      />,
    )
    return () => {
      setActions(null)
    }
  }, [selecting, sharing, data, setActions])

  /*
   * The page is the day's entries and nothing else: the day floats over its head, the day's
   * figures beside it, and the days either side of it at the window's edges. The room at the
   * foot is what lets the last entry scroll clear of the head where the head lies along it,
   * which is what a window too narrow for a column beside the day does.
   */
  return (
    <div
      className={
        // The day, and from `lg` the head's column beside it: the day narrows and stands with
        // its centre 8.5rem right of the window's, which is where the right half of the pair
        // falls. Margins rather than a transform, since a transform here would become the
        // containing block of every fixed thing the day floats.
        'mx-auto max-w-3xl space-y-6 pb-80 lg:mr-auto lg:ml-[calc(50%-12.5rem)] lg:max-w-2xl lg:pb-24'
      }
    >
      <DiaryDay
        day={day}
        data={data}
        trainedDays={trainedDays}
        incomplete={incomplete}
        marking={marking}
        onGo={go}
        onIncomplete={(next) => {
          void markIncomplete(next)
        }}
      />
      <EdgeSteps left={neighbour(-1)} right={neighbour(1)} />

      <DiaryHead
        ref={head}
        total={total}
        goal={goal}
        incomplete={incomplete}
        day={day}
        spans={laidOut}
      />

      {/*
       * The day itself, keyed on the day so a day moved to arrives as a new one, sliding in
       * from the side it lies on — the way the phone's deck turns.
       */}
      <div
        key={day}
        ref={laidOut}
        className={
          cameFrom === 'right'
            ? 'diary-slide-left'
            : cameFrom === 'left'
              ? 'diary-slide-right'
              : undefined
        }
      >
        {laid.length === 0 ? (
          <EmptyState
            icon={<DiaryIcon className="size-8" />}
            title="Nothing logged"
            detail={
              day === today
                ? 'Nothing eaten today has been logged yet.'
                : 'Nothing was logged on this day.'
            }
          />
        ) : (
          <section aria-label="Entries" className="space-y-8">
            {hours.map(({ hour, items }) => {
              const eatenThen = items
                .map((item) => item.entry)
                .filter((entry): entry is Entry => entry !== null)
              return (
                <section key={hour} aria-label={formatHour(hour)} className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3 px-1">
                    <h2 className="text-sm font-medium text-ink-muted">{formatHour(hour)}</h2>
                    {/* What the hour came to, in the figures an entry says about itself. */}
                    {eatenThen.length > 0 && (
                      <span className="truncate text-xs text-ink-muted">
                        {macroLine(dayTotal(eatenThen))}
                      </span>
                    )}
                  </div>
                  <ul className="divide-y divide-white/5 overflow-hidden rounded-2xl bg-surface-raised ring-1 ring-white/10">
                    {items.map(({ entry, workout }) => {
                      if (workout !== null) {
                        return (
                          <li key={workout.id}>
                            <WorkoutRow workout={workout} />
                          </li>
                        )
                      }
                      if (entry === null) {
                        return null
                      }
                      const on = chosen.has(entry.id)
                      /*
                       * The mark is the way to choose the entry, and it sits beside the row rather than
                       * inside it: a button inside a button is a press with two answers. The row is
                       * the way into the entry — or, while the choosing is open, another way to choose
                       * it, so a whole row is a target and not only the mark on it.
                       */
                      return (
                        <li
                          key={entry.id}
                          className={`flex items-center gap-3 px-4 transition-colors hover:bg-hover ${on ? 'bg-accent/10' : ''}`}
                        >
                          <button
                            type="button"
                            aria-label={`Select ${entry.name}`}
                            aria-pressed={on}
                            className="group/mark relative shrink-0 rounded-full"
                            onClick={() => {
                              toggle(entry)
                            }}
                          >
                            {entry.food === null ? (
                              <FoodMark name={entry.name} />
                            ) : (
                              <FoodMark food={entry.food} />
                            )}
                            {/* The check over the face: there for the pointer, and there for good once chosen. */}
                            <span
                              aria-hidden
                              className={`absolute inset-0 grid place-items-center rounded-full transition-opacity duration-150 ${
                                on
                                  ? 'bg-accent text-accent-ink opacity-100'
                                  : 'bg-black/50 text-white opacity-0 group-hover/mark:opacity-100 group-focus-visible/mark:opacity-100'
                              }`}
                            >
                              <CheckIcon className="size-5" />
                            </span>
                          </button>
                          <button
                            type="button"
                            aria-label={`${entry.name}${
                              entry.food === null ? '' : `, ${amountSaid(entry.amount)}`
                            }, ${macroLine(entry.nutrients)}`}
                            {...(selecting ? { 'aria-pressed': on } : {})}
                            className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left"
                            onClick={() => {
                              if (selecting) {
                                toggle(entry)
                              } else {
                                setOpened(entry)
                              }
                            }}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-lg">
                                {/*
                                  Isolated: a name written right to left and an amount written
                                  left to right are one run of text unless they are told they
                                  are two, and one run is reordered whole — which is how
                                  "חלב 1% · 200 g" comes out as "200 · 1% חלב g".
                                */}
                                <bdi>{entry.name}</bdi>
                                {/*
                          A quick add is always one serving of itself, so saying so says
                          nothing: the amount is only worth the words where there was a choice.
                        */}
                                {entry.food !== null && (
                                  <span className="text-ink-muted">
                                    {' · '}
                                    <bdi>{amountSaid(entry.amount)}</bdi>
                                  </span>
                                )}
                              </span>
                              <span className="block truncate text-sm text-ink-muted">
                                {macroLine(entry.nutrients)}
                              </span>
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </section>
        )}
      </div>

      {/*
       * An entry opened again: the food's own page, the one page a food stands on however it
       * was reached — the nutrition spec, A food's page — with the entry's amount and when it
       * stands on the foot, and taking it off the day in the heading. A quick entry, having no
       * food behind it, opens as the form it was typed on; and one brought in from another app
       * opens to be read and removed, being nothing that can be changed.
       */}
      {opened !== null &&
        (opened.food === null || isOnlyBroughtIn(opened.food) ? (
          <EntryDialog
            entry={opened}
            dayEntries={entries}
            goal={goal}
            onClose={() => {
              setOpened(null)
            }}
          />
        ) : (
          <FoodSheet
            key={opened.id}
            food={opened.food}
            mine={opened.food.source === 'own'}
            editing={{
              entry: opened,
              entries,
              goal,
              data,
              onSave: (amount, onDay, time) => saveEntry(opened, amount, onDay, time),
              onRemove: () => removeEntry(opened),
            }}
            onClose={() => {
              setOpened(null)
            }}
          />
        ))}
      {makingRecipe && (
        <RecipeFormDialog
          seed={ingredientsOf(picked)}
          onClose={() => {
            setMakingRecipe(false)
          }}
          onSaved={async (food) => {
            // The key that saved it goes on working until the entries it stands in for are in.
            await collapseInto(food)
            setMakingRecipe(false)
          }}
        />
      )}
      {/*
       * A meal made of taken-up entries exists at once, named after them and holding them as
       * slots; its first log is those entries, at the time of the earliest of them, and the
       * day stays as it was — the nutrition spec, How a meal comes to be. Its page then opens.
       */}
      {makingMeal !== null && (
        <MealPage
          key={makingMeal.id}
          meal={makingMeal}
          exists
          day={day}
          entries={entries}
          goal={goal}
          onClose={() => {
            setMakingMeal(null)
          }}
          onLogged={() => {
            setMakingMeal(null)
          }}
          onDeleted={() => {
            setMakingMeal(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * What can be done to the chosen entries, on the header — the nutrition spec, The diary. None
 * says how many it will act on: the header says that once, beside the way out. Each stands
 * as a glyph, and pointing at one, or reaching it with the keyboard, draws its name out
 * beside it, so a row of three glyphs takes no more room than it needs and still says what
 * each is for the moment somebody wonders. The name is in the document either way, for ears.
 */
function ChosenActions({
  data,
  sharing,
  onSend,
  onRecipe,
  onMeal,
  onSendToSomebody,
}: {
  data: NutritionData | null
  sharing: boolean
  onSend: (kind: SendKind, where: SendWhere) => Promise<void>
  onRecipe: () => void
  onMeal: () => Promise<void>
  onSendToSomebody: () => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <SendEntries data={data} onSend={onSend} />
      <ExpandingAction label="Make a recipe" Icon={RecipeIcon} onPress={onRecipe} />
      <ExpandingAction label="Make a meal" Icon={MealIcon} onPress={onMeal} />
      <ExpandingAction label="Send" Icon={SendIcon} working={sharing} onPress={onSendToSomebody} />
    </div>
  )
}

/** Entries as the members of something made of them, in the amounts they were logged in. */
function ingredientsOf(from: readonly Entry[]): Ingredient[] {
  return from.map((entry) => ({
    name: entry.name,
    food: entry.food,
    amount: entry.amount,
    nutrients: entry.nutrients,
  }))
}

/** The clock time a workout began, in the same shape an entry's time is written in. */
function timeOf(at: number): string {
  const when = new Date(at)
  return `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`
}

/**
 * A workout standing among the day's food — the nutrition spec, Workouts on the day.
 *
 * Told apart from an entry without being read: a picture where an entry has its food mark, a
 * ground of its own, and a chevron saying it leads somewhere off this page. An entry is a
 * thing eaten and this is a thing done, and a day reads wrongly if the two look alike.
 */
function WorkoutRow({ workout }: { readonly workout: Workout }) {
  const api = useApi()
  const { sets } = useDataState()
  const named = workout.name ?? 'Workout'
  // The exercises in the order they were first done, which is the order the workout reads in.
  const exercises = [
    ...new Set(
      sets
        .filter((set) => set.workoutId === workout.id)
        .sort((a, b) => a.performedAt - b.performedAt)
        .map((set) => set.exerciseName),
    ),
  ]
  const minutes =
    workout.finishedAt === null ? 0 : Math.floor((workout.finishedAt - workout.startedAt) / 60000)
  const said = [exercises.join(' · '), minutes >= 1 ? `${String(minutes)} min` : '']
    .filter((part) => part !== '')
    .join(' · ')
  const frame = workoutTileIds(workout.id, sets)[0] ?? null

  return (
    <Link
      to="/workouts/$workoutId"
      params={{ workoutId: workout.id }}
      aria-label={`${named} workout${said === '' ? '' : `, ${said}`}`}
      className="flex w-full items-center gap-3 bg-accent/10 px-4 py-3 text-left transition-colors hover:bg-accent/20"
    >
      <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-surface">
        {frame === null ? (
          <BarbellIcon aria-hidden className="size-5 text-accent" />
        ) : (
          <img src={api.mediaUrl(frame, 'thumbnail')} alt="" className="size-full object-cover" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-lg">{named}</span>
        {said !== '' && <span className="block truncate text-sm text-ink-muted">{said}</span>}
      </span>
      <ChevronRightIcon aria-hidden className="size-5 shrink-0 text-ink-muted" />
    </Link>
  )
}
